import type {
  CreateLobbyRequest,
  CreateLobbyResponse,
  InProgressMatchItem,
  JoinLobbyByCodeRequest,
  JoinLobbyByCodeResponse,
  LobbyListItem,
  MatchHistoryFilter,
  MatchHistoryResponse,
  ResumeMatchResponse,
} from "@fortress/shared";
import { httpBase } from "../net/origin";
import { UnauthorizedError } from "./api";
import { withAuthRetry } from "./session";
import { getAppState } from "../appState";

/**
 * Authenticated fetch for the lobby endpoints: bearer JWT only. A 401 becomes
 * `UnauthorizedError`, which `withAuthRetry` turns into one refresh + retry —
 * the refresh token itself never travels on these requests. With AUTH_DISABLED
 * there is no auth header at all.
 */
async function authedFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const { authDisabled } = await getAppState();
  const base = { "Content-Type": "application/json" };
  if (authDisabled) {
    return fetch(`${httpBase()}${path}`, { ...init, headers: base });
  }
  return withAuthRetry(async (accessToken) => {
    const res = await fetch(`${httpBase()}${path}`, {
      ...init,
      headers: { ...base, Authorization: `Bearer ${accessToken}` },
    });
    if (res.status === 401) throw new UnauthorizedError();
    return res;
  });
}

async function parseError(res: Response): Promise<string> {
  const detail = await res.json().catch(() => ({}));
  return (detail as { error?: string }).error ?? `HTTP ${res.status}`;
}

/** Create a lobby room; client should then `joinById`. */
export async function createLobby(
  body: CreateLobbyRequest,
): Promise<CreateLobbyResponse> {
  const res = await authedFetch("/lobbies", {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<CreateLobbyResponse>;
}

/** Public lobby browser rows. */
export async function listLobbies(): Promise<LobbyListItem[]> {
  const res = await authedFetch("/lobbies");
  if (!res.ok) throw new Error(await parseError(res));
  const body = (await res.json()) as { lobbies?: LobbyListItem[] };
  return body.lobbies ?? [];
}

/** This account's in-progress matches — each either RECONNECTable (a live
 * room is hosting it) or RESUMEable (no live room; revive from the save). */
export async function listMyMatches(): Promise<InProgressMatchItem[]> {
  const res = await authedFetch("/games/mine");
  if (!res.ok) throw new Error(await parseError(res));
  const body = (await res.json()) as { matches?: InProgressMatchItem[] };
  return body.matches ?? [];
}

/** One page of this account's finished matches, newest first. */
export async function listMatchHistory(opts: {
  status: MatchHistoryFilter;
  page: number;
}): Promise<MatchHistoryResponse> {
  const res = await authedFetch(
    `/games/history?status=${opts.status}&page=${opts.page}`,
  );
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<MatchHistoryResponse>;
}

/** Revive a saved match into a live room (idempotent); join the roomId next. */
export async function resumeMatch(gameId: string): Promise<ResumeMatchResponse> {
  const res = await authedFetch(`/games/${encodeURIComponent(gameId)}/resume`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<ResumeMatchResponse>;
}

/** Mark a paused saved match as canceled; its document remains persisted. */
export async function cancelMatch(gameId: string): Promise<void> {
  const res = await authedFetch(`/games/${encodeURIComponent(gameId)}/cancel`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(await parseError(res));
}

/** Resolve an invite code to a roomId. */
export async function joinLobbyByCode(
  body: JoinLobbyByCodeRequest,
): Promise<JoinLobbyByCodeResponse> {
  const res = await authedFetch("/lobbies/join", {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<JoinLobbyByCodeResponse>;
}
